<script setup>
import { computed } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import TourBookingStatusPill from '@/Components/UI/TourBookingStatusPill.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()

const props = defineProps({ booking: { type: Object, required: true } })
const can = computed(() => props.booking.can || {})

function transition(verb) {
  router.patch(`/tour_bookings/${props.booking.id}/${verb}`, {}, { preserveScroll: true })
}
</script>

<template>
  <PageShell
    :title="booking.tour_title"
    :subtitle="booking.customer_name"
    back-href="/tour_calendar"
  >
    <template #actions>
      <Link
        v-if="can.edit"
        :href="`/tour_bookings/${booking.id}/edit`"
        class="inline-flex items-center"
      >
        <UiButton variant="secondary">
          {{ t('common.edit') }}
        </UiButton>
      </Link>
    </template>

    <div class="grid gap-4 md:grid-cols-3">
      <UiCard class="md:col-span-2">
        <dl class="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('tourBookings.status') }}
            </dt>
            <dd class="mt-0.5">
              <TourBookingStatusPill :status="booking.status" />
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('tourBookings.vendor') }}
            </dt>
            <dd class="fw-value mt-0.5">
              {{ booking.vendor_name }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('tourBookings.dates') }}
            </dt>
            <dd class="fw-value mt-0.5">
              {{ formatDate(booking.starts_on) }} – {{ formatDate(booking.ends_on) }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('tourBookings.pax') }}
            </dt>
            <dd class="fw-value mt-0.5">
              {{ booking.adults }} + {{ booking.children }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('tourBookings.price') }}
            </dt>
            <dd class="fw-value tabular-nums mt-0.5">
              {{ formatMoney(booking.price_quoted_cents) }}
            </dd>
          </div>
          <div>
            <dt class="text-[color:var(--muted)]">
              {{ t('tourBookings.createdBy') }}
            </dt>
            <dd class="fw-value mt-0.5">
              {{ booking.created_by }}
            </dd>
          </div>
          <div
            v-if="booking.notes"
            class="col-span-2"
          >
            <dt class="text-[color:var(--muted)]">
              {{ t('tourBookings.notes') }}
            </dt>
            <dd class="mt-0.5 whitespace-pre-line">
              {{ booking.notes }}
            </dd>
          </div>
        </dl>
      </UiCard>

      <UiCard>
        <h3 class="text-sm font-semibold mb-3">
          {{ t('tourBookings.quickActions') }}
        </h3>
        <div class="flex flex-col gap-2">
          <UiButton
            v-if="can.confirm"
            variant="primary"
            class="w-full"
            @click="transition('confirm')"
          >
            {{ t('tourBookings.confirm') }}
          </UiButton>
          <UiButton
            v-if="can.complete"
            variant="primary"
            class="w-full"
            @click="transition('complete')"
          >
            {{ t('tourBookings.complete') }}
          </UiButton>
          <UiButton
            v-if="can.cancel"
            variant="danger"
            class="w-full"
            @click="transition('cancel')"
          >
            {{ t('tourBookings.cancel') }}
          </UiButton>
        </div>
      </UiCard>
    </div>
  </PageShell>
</template>
